// ConversationTitle.tsx
import {useEffect, useId, useRef, useState} from 'react'
import {useTheme} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import {useConversationEvents} from './ConversationStreamProvider'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'

interface ConversationTitleProps {
  initialTitle?: string
  /** when true, subscribes to WS updates; typing animates only on WS updates */
  live?: boolean
}

const ConversationTitle: React.FC<ConversationTitleProps> = ({initialTitle = '', live = false}) => {
  const [t] = useCustomTranslation('alpinist')
  const [title, setTitle] = useState(initialTitle || '')
  const [displayTitle, setDisplayTitle] = useState(initialTitle || '')
  const [loading, setLoading] = useState(false)
  const [animate, setAnimate] = useState(false) // ← only true when WS updates the title
  const theme = useTheme()
  const gradId = useId()
  const typingRef = useRef<number | null>(null)

  // keep local state synced to prop (never animate on prop changes)
  useEffect(() => {
    setTitle(initialTitle || '')
    setDisplayTitle(initialTitle || '')
    setAnimate(false)
  }, [initialTitle])

  // subscribe to WS events ONLY in live mode
  const {subscribe} = useConversationEvents()
  useEffect(() => {
    if (!live || !subscribe) {
      return
    }

    const off = subscribe({
      onTitleStarted: () => setLoading(true),
      onTitleUpdated: (payload) => {
        const next = payload.title
        setLoading(false)
        setAnimate(true) // animate only on WS-driven changes
        setTitle(next) // <-- set the actual string, not the whole object
      },
      onError: () => setLoading(false),
    })
    return off
  }, [live, subscribe])

  // typing effect only when `animate` is true (WS-driven)
  useEffect(() => {
    if (!live) {
      return
    }

    // if not animating, just show the title plainly
    if (!animate) {
      setDisplayTitle(title || '')
      return
    }

    // WS-triggered typing animation
    if (!title) {
      setDisplayTitle('')
      setAnimate(false)
      return
    }

    let i = 0
    setDisplayTitle('')

    if (typingRef.current) {
      clearInterval(typingRef.current)
    }

    typingRef.current = window.setInterval(() => {
      i++
      setDisplayTitle(title.slice(0, i))
      if (i >= title.length && typingRef.current) {
        clearInterval(typingRef.current)
        typingRef.current = null
        setAnimate(false) // animation done
      }
    }, 40)

    return () => {
      if (typingRef.current) {
        clearInterval(typingRef.current)
        typingRef.current = null
      }
    }
  }, [live, title, animate])

  const shownTitle = (displayTitle || '').trim() || t('conversation_untitled')

  return (
    <span
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: theme.palette.text.primary,
      }}
    >
      {live && loading && (
        <EditIcon
          sx={{
            fontSize: 13,
            opacity: 0.9,
            animation: 'pulse 1.5s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
          }}
          style={{fill: `url(#${gradId})`}}
        />
      )}

      {shownTitle}

      {/* gradient defs */}
      <svg width="0" height="0" style={{position: 'absolute'}}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0e9afa" />
            <stop offset="50%" stopColor="#8f3fd5" />
            <stop offset="100%" stopColor="#e100ff" />
          </linearGradient>
        </defs>
      </svg>

      <style>
        {`
          @keyframes pulse {
            0%   { transform: scale(1);   opacity: 0.9; }
            50%  { transform: scale(1.15); opacity: 0.55; }
            100% { transform: scale(1);   opacity: 0.9; }
          }
        `}
      </style>
    </span>
  )
}

export default ConversationTitle
